import { useState } from 'react';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import {
  MoreHorizontal,
  Eye,
  Shield,
  Ban,
  UserCircle,
  LibraryBig,
  RotateCcw,
  ChevronLeft,
  ChevronRight,
} from 'lucide-react';
import { format } from 'date-fns';
import type { User, PaginatedResponse, ResetResult } from '@/types';
import { UserDetailDialog } from './UserDetailDialog';
import { RoleUpdateDialog } from './RoleUpdateDialog';
import { ConfirmationDialog } from './ConfirmationDialog';
import { AssignPersonaDialog } from './AssignPersonaDialog';
import { MapStoryCardsDialog } from './MapStoryCardsDialog';
import { ResetAgentDataDialog } from './ResetAgentDataDialog';
import { apiClient } from '@/services/api-client';

interface UsersTableProps {
  data: PaginatedResponse<User> | null;
  isLoading: boolean;
  page: number;
  onPageChange: (page: number) => void;
  onRefresh?: () => void;
}

export function UsersTable({ data, isLoading, page, onPageChange, onRefresh }: UsersTableProps) {
  const [selectedUser, setSelectedUser] = useState<User | null>(null);
  const [detailOpen, setDetailOpen] = useState(false);
  const [roleDialogOpen, setRoleDialogOpen] = useState(false);
  const [disableDialogOpen, setDisableDialogOpen] = useState(false);
  const [personaDialogOpen, setPersonaDialogOpen] = useState(false);
  const [storyCardDialogOpen, setStoryCardDialogOpen] = useState(false);
  const [resetDialogOpen, setResetDialogOpen] = useState(false);
  const [isDisableLoading, setIsDisableLoading] = useState(false);
  const [resetNotice, setResetNotice] = useState<{
    title: string;
    description: string;
    warnings?: string[];
    variant?: 'success' | 'destructive';
  } | null>(null);

  const handleDisableToggle = async () => {
    if (!selectedUser) return;
    setIsDisableLoading(true);
    try {
      const response = await apiClient.put(`/api/v1/admin/users/${selectedUser.id}/status`, {
        isDisabled: !selectedUser.isDisabled,
      });
      if (response.success) {
        setDisableDialogOpen(false);
        onRefresh?.();
      }
    } catch {
      // Network error handled by API client
    } finally {
      setIsDisableLoading(false);
    }
  };

  if (isLoading) {
    return (
      <div className="space-y-4">
        {[...Array(5)].map((_, i) => (
          <div key={i} className="h-14 animate-pulse rounded bg-muted" />
        ))}
      </div>
    );
  }

  if (!data || data.items.length === 0) {
    return (
      <div className="flex h-40 items-center justify-center text-muted-foreground">
        No users found
      </div>
    );
  }

  const roleBadgeVariant = (role: string) => {
    switch (role) {
      case 'super_admin':
        return 'destructive' as const;
      case 'admin':
        return 'default' as const;
      default:
        return 'secondary' as const;
    }
  };

  const isResetEligible = (user: User) =>
    user.role === 'user' && user.email.toLowerCase().endsWith('@yopmail.com');

  const handleResetSuccess = (result: ResetResult) => {
    const { counts } = result;
    setResetNotice({
      title: 'Reset complete',
      description: `${counts.sessions} sessions, ${counts.memories} memories, ${counts.sessionMessages} messages, and ${counts.storyCardProgress} story-card progress entries reset.`,
      warnings: result.warnings,
      variant: result.warnings.length ? 'destructive' : 'success',
    });
    onRefresh?.();
  };

  return (
    <>
      <div className="rounded-md border">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>User</TableHead>
              <TableHead>Role</TableHead>
              <TableHead>Status</TableHead>
              <TableHead>Joined</TableHead>
              <TableHead className="w-[70px]"></TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {data.items.map((user) => (
              <TableRow key={user.id} className={user.isDisabled ? 'opacity-60' : undefined}>
                <TableCell>
                  <div className="flex items-center gap-3">
                    <Avatar className="h-8 w-8">
                      <AvatarImage src={user.profileImageUrl || undefined} />
                      <AvatarFallback className="bg-orb-gold/10 text-orb-gold text-xs">
                        {user.firstName?.[0] || user.email[0].toUpperCase()}
                      </AvatarFallback>
                    </Avatar>
                    <div>
                      <p className="font-medium text-foreground">
                        {user.firstName && user.lastName
                          ? `${user.firstName} ${user.lastName}`
                          : user.email}
                      </p>
                      <p className="text-sm text-muted-foreground">{user.email}</p>
                    </div>
                  </div>
                </TableCell>
                <TableCell>
                  <Badge variant={roleBadgeVariant(user.role)}>
                    {user.role.replace(/_/g, ' ')}
                  </Badge>
                </TableCell>
                <TableCell>
                  {user.isDisabled ? (
                    <Badge variant="destructive">Disabled</Badge>
                  ) : (
                    <Badge variant="success">Active</Badge>
                  )}
                </TableCell>
                <TableCell className="text-muted-foreground">
                  {format(new Date(user.createdAt), 'MMM d, yyyy')}
                </TableCell>
                <TableCell>
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <Button variant="ghost" size="icon">
                        <MoreHorizontal className="h-4 w-4" />
                      </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end">
                      <DropdownMenuLabel>Actions</DropdownMenuLabel>
                      <DropdownMenuSeparator />
                      <DropdownMenuItem
                        onClick={() => {
                          setSelectedUser(user);
                          setDetailOpen(true);
                        }}
                      >
                        <Eye className="mr-2 h-4 w-4" />
                        View Details
                      </DropdownMenuItem>
                      <DropdownMenuItem
                        onClick={() => {
                          setSelectedUser(user);
                          setRoleDialogOpen(true);
                        }}
                      >
                        <Shield className="mr-2 h-4 w-4" />
                        Change Role
                      </DropdownMenuItem>
                      {user.role === 'user' && !user.persona && (
                        <DropdownMenuItem
                          onClick={() => {
                            setSelectedUser(user);
                            setPersonaDialogOpen(true);
                          }}
                        >
                          <UserCircle className="mr-2 h-4 w-4" />
                          Assign Persona
                        </DropdownMenuItem>
                      )}
                      {user.role === 'user' && user.persona && (
                        <DropdownMenuItem
                          onClick={() => {
                            setSelectedUser(user);
                            setPersonaDialogOpen(true);
                          }}
                        >
                          <UserCircle className="mr-2 h-4 w-4" />
                          View Persona
                        </DropdownMenuItem>
                      )}
                      {user.role === 'user' && (
                        <DropdownMenuItem
                          onClick={() => {
                            setSelectedUser(user);
                            setStoryCardDialogOpen(true);
                          }}
                        >
                          <LibraryBig className="mr-2 h-4 w-4" />
                          Map Story Cards
                        </DropdownMenuItem>
                      )}
                      {isResetEligible(user) && (
                        <DropdownMenuItem
                          className="text-destructive focus:text-destructive"
                          onClick={() => {
                            setSelectedUser(user);
                            setResetDialogOpen(true);
                          }}
                        >
                          <RotateCcw className="mr-2 h-4 w-4" />
                          Reset agent data
                        </DropdownMenuItem>
                      )}
                      <DropdownMenuSeparator />
                      <DropdownMenuItem
                        onClick={() => {
                          setSelectedUser(user);
                          setDisableDialogOpen(true);
                        }}
                      >
                        <Ban className="mr-2 h-4 w-4" />
                        {user.isDisabled ? 'Enable Access' : 'Disable Access'}
                      </DropdownMenuItem>
                    </DropdownMenuContent>
                  </DropdownMenu>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>

      {/* Pagination */}
      <div className="flex items-center justify-between px-2 py-4">
        <p className="text-sm text-muted-foreground">
          Showing {(page - 1) * data.meta.limit + 1} to{' '}
          {Math.min(page * data.meta.limit, data.meta.total)} of {data.meta.total} users
        </p>
        <div className="flex items-center gap-2">
          <Button
            variant="outline"
            size="sm"
            onClick={() => onPageChange(page - 1)}
            disabled={page <= 1}
          >
            <ChevronLeft className="h-4 w-4" />
            Previous
          </Button>
          <span className="text-sm text-muted-foreground">
            Page {page} of {data.meta.totalPages}
          </span>
          <Button
            variant="outline"
            size="sm"
            onClick={() => onPageChange(page + 1)}
            disabled={page >= data.meta.totalPages}
          >
            Next
            <ChevronRight className="h-4 w-4" />
          </Button>
        </div>
      </div>

      {/* Dialogs */}
      <UserDetailDialog
        userId={selectedUser?.id ?? null}
        open={detailOpen}
        onOpenChange={setDetailOpen}
      />
      <RoleUpdateDialog
        user={selectedUser}
        open={roleDialogOpen}
        onOpenChange={setRoleDialogOpen}
        onSuccess={onRefresh}
      />
      <AssignPersonaDialog
        user={selectedUser}
        open={personaDialogOpen}
        onOpenChange={setPersonaDialogOpen}
        onSuccess={onRefresh}
      />
      <MapStoryCardsDialog
        user={selectedUser}
        open={storyCardDialogOpen}
        onOpenChange={setStoryCardDialogOpen}
        onSuccess={onRefresh}
      />
      <ResetAgentDataDialog
        user={selectedUser}
        open={resetDialogOpen}
        onOpenChange={setResetDialogOpen}
        onSuccess={handleResetSuccess}
        onForbidden={() =>
          setResetNotice({
            title: 'User is not eligible for reset',
            description: 'Only test users with a @yopmail.com email and user role can be reset.',
            variant: 'destructive',
          })
        }
      />
      <ConfirmationDialog
        open={disableDialogOpen}
        onOpenChange={setDisableDialogOpen}
        title={selectedUser?.isDisabled ? 'Enable User Access' : 'Disable User Access'}
        description={
          selectedUser?.isDisabled
            ? `Are you sure you want to re-enable access for ${selectedUser?.firstName || selectedUser?.email}? They will be able to log in again.`
            : `Are you sure you want to disable access for ${selectedUser?.firstName || selectedUser?.email}? They will be unable to log in until re-enabled.`
        }
        confirmText={selectedUser?.isDisabled ? 'Enable Access' : 'Disable Access'}
        variant={selectedUser?.isDisabled ? 'default' : 'destructive'}
        isLoading={isDisableLoading}
        onConfirm={handleDisableToggle}
      />
      {resetNotice && (
        <div
          role="status"
          className={`fixed bottom-4 right-4 z-50 max-w-md rounded-lg border bg-background p-4 shadow-lg ${
            resetNotice.variant === 'destructive' ? 'border-destructive/40' : 'border-orb-teal/40'
          }`}
        >
          <div className="flex items-start justify-between gap-4">
            <div>
              <p
                className={`font-medium ${
                  resetNotice.variant === 'destructive' ? 'text-destructive' : 'text-orb-teal'
                }`}
              >
                {resetNotice.title}
              </p>
              <p className="mt-1 text-sm text-muted-foreground">{resetNotice.description}</p>
              {resetNotice.warnings && resetNotice.warnings.length > 0 && (
                <details className="mt-2 text-sm">
                  <summary className="cursor-pointer text-destructive">Cleanup warnings</summary>
                  <ul className="mt-2 space-y-1 text-muted-foreground">
                    {resetNotice.warnings.map((warning) => (
                      <li key={warning}>{warning}</li>
                    ))}
                  </ul>
                </details>
              )}
            </div>
            <Button variant="ghost" size="sm" onClick={() => setResetNotice(null)}>
              Dismiss
            </Button>
          </div>
        </div>
      )}
    </>
  );
}
